import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { History, ChevronDown, ChevronUp, User, FileText, Calendar, Edit2, Plus, Trash2, Clock } from 'lucide-react';

const ActivityLog = ({ projectId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (isExpanded) {
      // Initial fetch with loading indicator
      setLoading(true);
      fetchActivities();
      // Poll for new activities every 5 seconds when expanded
      pollIntervalRef.current = setInterval(fetchActivities, 5000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [projectId, isExpanded]);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/activity`);
      // Only update state if data actually changed (compare by length and first item id)
      const newData = response.data;
      if (newData.length !== activities.length || 
          (newData.length > 0 && activities.length > 0 && newData[0].id !== activities[0].id)) {
        setActivities(newData);
      }
    } catch (error) {
      console.error('Failed to fetch activities', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    return date.toLocaleDateString('de-CH', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'task_created': return <Plus className="w-3 h-3 text-green-500" />;
      case 'task_updated': return <Edit2 className="w-3 h-3 text-blue-500" />;
      case 'task_deleted': return <Trash2 className="w-3 h-3 text-red-500" />;
      case 'task_completed': return <FileText className="w-3 h-3 text-green-500" />;
      case 'comment_added': return <FileText className="w-3 h-3 text-purple-500" />;
      case 'time_logged': return <Clock className="w-3 h-3 text-orange-500" />;
      case 'assignee_changed': return <User className="w-3 h-3 text-blue-500" />;
      default: return <History className="w-3 h-3 text-muted-foreground" />;
    }
  };

  return (
    <Card data-testid="activity-log-card">
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Aktivitäten
            {activities.length > 0 && (
              <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
                {activities.length}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="max-h-[250px] overflow-y-auto space-y-2">
            {loading ? (
              <div className="text-center text-sm text-muted-foreground py-4">
                Laden...
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-4">
                <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Noch keine Aktivitäten
              </div>
            ) : (
              activities.map((activity) => (
                <div 
                  key={activity.id} 
                  className="flex items-start gap-2 p-2 rounded-lg hover:bg-muted/50 text-xs"
                >
                  <div className="mt-0.5">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground">
                      <span className="font-medium">{activity.user_name}</span>
                      {' '}{activity.description}
                    </p>
                    <p className="text-muted-foreground text-[10px] mt-0.5">
                      {formatTime(activity.created_at)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ActivityLog;
